import { keepPreviousData, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { BadgeCheck, Globe, History, MonitorSmartphone, ScanLine, ShieldAlert, ShieldCheck, TriangleAlert } from "lucide-react";
import { Link } from "react-router";
import { useState } from "react";
import { ChangePasswordForm, cn, ErrorState, formatDateTime, formatRelative, humanize, Pagination, SessionsPanel, Skeleton, toast, TwoFactorPanel } from "@hostdock/ui";
import type { WebsiteScanSummary } from "@hostdock/types";
import { api, SESSION_KEY } from "../api";
import { scanResultLabel, threats, toReview } from "../lib/scan-result";
import { Banner, Cell, DetailCard, PageHero, Pill, rowPrimaryClass, rowSecondaryClass, SectionCard, StatCard, tableCell, TableShell } from "../components/page-kit";
import { useSession, useSetSession } from "../session";
import { ACTOR_LABEL } from "./home";

const PAGE_SIZE = 20;
const scanBusy = (s: string) => s === "PENDING" || s === "RUNNING";

export function SecurityPage() {
  const { session, logout } = useSession();
  const setSession = useSetSession();
  const qc = useQueryClient();
  const home = useQuery({ queryKey: ["home"], queryFn: api.home });
  const scans = useQuery({ queryKey: ["security", "scans"], queryFn: api.security.scans, refetchInterval: (s) => (s.state.data?.items.some((r) => r.latest && scanBusy(r.latest.status)) ? 3000 : false) });
  const mfa = session.user.mfaEnabled;
  const sites = scans.data?.items;
  const done = (r: WebsiteScanSummary) => r.latest?.status === "COMPLETED" || r.latest?.status === "PARTIAL";
  const infected = sites?.filter((r) => done(r) && threats(r.latest)).length ?? 0;
  const clean = sites?.filter((r) => done(r) && !threats(r.latest) && !toReview(r.latest)).length ?? 0;
  const status = session.customer.status.charAt(0) + session.customer.status.slice(1).toLowerCase();
  const goToTwoFactor = () => document.getElementById("two-factor")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="flex flex-col lg:px-[13px]">
      <PageHero icon={ShieldCheck} title="Security" description="Protect your account, scan your websites for malware and review sign-in activity." />
      {!mfa && (
        <Banner icon={ShieldAlert} title={session.mfaRequired ? "Two-factor authentication is required for your account" : "Two-factor authentication is off"}
          action={<button type="button" onClick={goToTwoFactor} className={rowPrimaryClass}>Set it up</button>}>
          A code from an authenticator app keeps your account safe even if someone learns your password.
        </Banner>
      )}
      {infected > 0 && (
        <Banner tone="bad" icon={TriangleAlert} title={`${infected} website${infected === 1 ? " has" : "s have"} infected files`}>
          The latest scan found malicious or likely malicious files. Open the website below to see each file and why it was flagged.
        </Banner>
      )}

      <div className="mt-[10px] grid grid-cols-2 gap-[11px] xl:grid-cols-4">
        <StatCard icon={ShieldCheck} tile={mfa ? "green" : "orange"} label="Two-factor" value={mfa ? "Enabled" : "Off"} sub={mfa ? "Codes from your authenticator app" : session.mfaRequired ? "Required for your account" : "Recommended"} />
        <StatCard icon={MonitorSmartphone} tile="blue" label="Sessions" value={home.data ? home.data.security.activeSessions : "—"} sub="Browsers signed in to your account" />
        <StatCard icon={ScanLine} tile={infected ? "orange" : "teal"} label="Clean sites" value={sites ? `${clean} / ${sites.length}` : "—"} sub={!sites ? undefined : infected ? `${infected} with infected files` : sites.length - clean ? `${sites.length - clean} not scanned yet` : "In their latest scan"} />
        <StatCard icon={BadgeCheck} tile="green" label="Account" value={status} sub={session.customer.displayName} />
      </div>

      <MalwareScans q={scans} />

      <div className="mt-[15px] grid items-start gap-[15px] xl:grid-cols-2">
        <div id="two-factor" className="scroll-mt-20">
          <DetailCard title="Two-factor authentication" description="Ask for a code from your authenticator app when you sign in.">
            <TwoFactorPanel api={api.account} enabled={mfa} required={session.mfaRequired} onChanged={() => void qc.invalidateQueries({ queryKey: SESSION_KEY })} />
          </DetailCard>
        </div>
        <DetailCard title="Password" description="Changing your password signs out your other sessions.">
          <div className="max-w-sm">
            <ChangePasswordForm
              api={api.account}
              onChanged={(s) => {
                setSession(s);
                toast.success("Password changed");
                void qc.invalidateQueries({ queryKey: ["account", "sessions"] });
              }}
            />
          </div>
        </DetailCard>
      </div>

      <DetailCard className="mt-[15px]" title="Signed-in sessions" description="Sign out any browser you don't recognise." flush>
        <SessionsPanel api={api.account} onSignedOut={() => void logout()} />
      </DetailCard>

      <AccountActivity />
    </div>
  );
}

function ScanState({ r }: { r: WebsiteScanSummary }) {
  const l = r.latest;
  if (!l) return <Pill tone="off" dot>Never scanned</Pill>;
  if (scanBusy(l.status)) return <Pill tone="info" dot>Scanning…</Pill>;
  if (l.status === "FAILED") return <Pill tone="bad" dot>Scan failed</Pill>;
  const res = scanResultLabel(l);
  return <Pill tone={res.tone} dot>{res.label}</Pill>;
}

/** Every website with its latest malware scan; each row opens that website's scanner. */
function MalwareScans({ q }: { q: UseQueryResult<{ items: WebsiteScanSummary[] }> }) {
  const items = q.data?.items;
  const anyAvailable = items?.some((r) => r.scanAvailable);
  const cols = "xl:grid xl:grid-cols-[minmax(0,1fr)_200px_190px_minmax(0,1fr)_130px] xl:items-center";
  return (
    <SectionCard title="Malware scanning" count={items?.length} description="Scan each website's files with ClamAV and DockShield. Findings are exactly what the scanners report, with the reason for each.">
      {q.error ? <div className="py-6"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>
        : q.isPending ? <Skeleton className="my-4 h-24 w-full rounded-xl" />
        : !items?.length ? (
          <div className="flex flex-col items-center px-6 py-9 text-center">
            <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-accent-soft text-accent"><ScanLine size={22} /></span>
            <p className="text-[14px] font-semibold">No websites yet</p>
            <p className="mt-1 text-[12.5px] text-muted">Create a website to scan it for malware.</p>
          </div>
        ) : (
          <TableShell flat label="Malware scans" cols={cols} headers={["Website", "Last scan", "When", "Scanner", "Actions"]}>
            {items.map((r) => (
              <li key={r.websiteId} role="row" aria-label={r.primaryDomain} className={cn(cols, "flex flex-col gap-3 py-4 text-[12.5px] text-fg xl:min-h-[66px] xl:gap-0 xl:py-[10px]")}>
                <div role="cell" className="flex min-w-0 items-center gap-[14px] xl:pr-3">
                  <span className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-xl bg-[#d6f5f7] text-[#0a8ea0] dark:bg-surface-3"><Globe size={19} strokeWidth={1.7} /></span>
                  <Link to={`/websites/${r.websiteId}`} className="min-w-0 truncate text-[13.5px] font-semibold hover:text-accent" title={r.primaryDomain}>{r.primaryDomain}</Link>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:contents">
                  <Cell label="Last scan" className={tableCell}><ScanState r={r} /></Cell>
                  <Cell label="When" className={tableCell}>
                    {r.latest ? <span title={formatDateTime(r.latest.createdAt)}>{formatRelative(r.latest.createdAt)}</span> : <span className="text-muted">—</span>}
                  </Cell>
                  <Cell label="Scanner" className={cn(tableCell, "col-span-2 sm:col-span-1")}>
                    {r.scanAvailable ? <span className="text-muted">Available on this website's server</span> : <span className="text-subtle">Not available on this website's server yet</span>}
                  </Cell>
                </div>
                <div role="cell" className="flex items-center gap-[10px] xl:h-[48px] xl:border-l xl:border-line xl:pl-[10px]">
                  {r.scanAvailable
                    ? <Link to={`/websites/${r.websiteId}?tab=security`} className={cn(rowPrimaryClass, "xl:w-[112px]")}><ScanLine size={15} className="shrink-0" />{r.latest ? "Scan again" : "Scan"}</Link>
                    : <Link to={`/websites/${r.websiteId}`} className={cn(rowSecondaryClass, "xl:w-[112px]")}>Open website</Link>}
                </div>
              </li>
            ))}
          </TableShell>
        )}
      {items && items.length > 0 && !anyAvailable && <p className="border-t border-line py-3 text-[12.5px] text-muted">Malware scanning is set up automatically on servers with enough memory. Contact support if you need it on yours.</p>}
    </SectionCard>
  );
}

function AccountActivity() {
  const [page, setPage] = useState(1);
  const activity = useQuery({ queryKey: ["activity", page], queryFn: () => api.activity({ page, pageSize: PAGE_SIZE }), placeholderData: keepPreviousData });
  const items = activity.data?.items;
  const cols = "xl:grid xl:grid-cols-[200px_minmax(0,1fr)_160px_170px_130px] xl:items-center";
  return (
    <SectionCard title="Account activity" count={activity.data?.total} description="Sign-ins and changes on your account and websites, newest first.">
      {activity.error ? <div className="py-6"><ErrorState error={activity.error} onRetry={() => void activity.refetch()} /></div>
        : activity.isPending ? <Skeleton className="my-4 h-24 w-full rounded-xl" />
        : !items?.length ? <p className="py-8 text-center text-[12.5px] text-muted">No activity yet. Account and website events will appear here.</p>
        : (
          <TableShell flat label="Account activity" cols={cols} headers={["Time", "Activity", "By", "IP address", "Result"]}>
            {items.map((r) => (
              <li key={r.id} role="row" className={cn(cols, "flex flex-col gap-2 py-3 text-[12.5px] text-fg xl:min-h-[50px] xl:gap-0 xl:py-[4px]")}>
                <div role="cell" className="flex items-center gap-[10px] text-muted xl:pr-3">
                  <History size={15} className="shrink-0 text-subtle" />
                  <span className="whitespace-nowrap" title={formatRelative(r.occurredAt)}>{formatDateTime(r.occurredAt)}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:contents">
                  <Cell label="Activity" className={cn(tableCell, "col-span-2 sm:col-span-1 xl:h-[40px]")}><span className="max-w-full truncate font-medium">{humanize(r.action)}</span></Cell>
                  <Cell label="By" className={cn(tableCell, "xl:h-[40px]")}><span>{ACTOR_LABEL[r.actor]}</span></Cell>
                  <Cell label="IP address" className={cn(tableCell, "xl:h-[40px]")}><span className="font-mono text-[12px]">{r.ip ?? "—"}</span></Cell>
                  <Cell label="Result" className={cn(tableCell, "xl:h-[40px]")}><Pill tone={r.result === "SUCCESS" ? "ok" : "bad"} dot>{humanize(r.result)}</Pill></Cell>
                </div>
              </li>
            ))}
          </TableShell>
        )}
      {activity.data && activity.data.total > PAGE_SIZE && <div className="border-t border-line"><Pagination page={page} pageSize={PAGE_SIZE} total={activity.data.total} onPage={setPage} /></div>}
    </SectionCard>
  );
}
